import { useEffect, useRef, useState } from "react";

/**
 * Splits a string into per-character spans that animate in one after another.
 * Triggers only once when the element scrolls into view (or on mount if `runOnMount`).
 */
export default function LetterReveal({
  text,
  delayBase = 0,
  stepMs = 45,
  className = "",
  italicWords = [],
  runOnMount = false,
  as = "span",
}) {
  const Tag = as;
  const ref = useRef(null);
  const [visible, setVisible] = useState(!!runOnMount);

  useEffect(() => {
    if (runOnMount) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [runOnMount]);

  const words = text.split(" ");

  return (
    <Tag ref={ref} className={`letter-reveal ${className}`}>
      {words.map((word, wi) => {
        const isItalic = italicWords.includes(word);
        return (
          <span
            key={wi}
            className={`whitespace-nowrap ${isItalic ? "italic" : ""}`}
            style={{ display: "inline-block" }}
          >
            {[...word].map((ch, ci) => {
              const flat = words.slice(0, wi).reduce((s, w) => s + w.length, 0) + ci;
              return (
                <span
                  key={ci}
                  style={{
                    animationDelay: visible ? `${delayBase + flat * stepMs}ms` : "9999s",
                  }}
                >
                  {ch}
                </span>
              );
            })}
            {wi < words.length - 1 && (
              <span style={{ display: "inline-block", width: "0.28em" }}> </span>
            )}
          </span>
        );
      })}
    </Tag>
  );
}
